import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowUpRight, PenLine, Search, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import { CATEGORIES, useLang } from "@/i18n";
import { BlogCard } from "@/components/BlogCard";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";

const docsImage = "https://images.unsplash.com/photo-1775163024488-e88e4a71179f?crop=entropy&cs=srgb&fm=jpg&q=85";
const emptyPost = { title: "", excerpt: "", content: "", category: "GST Updates", tags: "", title_hi: "", excerpt_hi: "", content_hi: "" };

function Writer({ onPublished, onCancel }) {
  const { t } = useLang();
  const [post, setPost] = useState(emptyPost);
  const [busy, setBusy] = useState(false);

  const publish = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        title: post.title,
        excerpt: post.excerpt,
        content: post.content,
        category: post.category,
        tags: post.tags.split(",").map((s) => s.trim()).filter(Boolean),
        title_hi: post.title_hi || null,
        excerpt_hi: post.excerpt_hi || null,
        content_hi: post.content_hi || null,
      };
      const { data } = await api.post("/blogs", payload);
      toast.success(t("writer.success"));
      onPublished(data);
    } catch {
      toast.error(t("writer.error"));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setPost({ ...post, [k]: e.target.value });
  return (
    <form className="writer" onSubmit={publish} data-testid="blog-writer-form">
      <div className="writer-grid">
        <input required placeholder={t("writer.title")} value={post.title} onChange={set("title")} data-testid="blog-title-input" />
        <input required placeholder={t("writer.excerpt")} value={post.excerpt} onChange={set("excerpt")} data-testid="blog-excerpt-input" />
        <select value={post.category} onChange={set("category")} data-testid="blog-category-select" aria-label={t("writer.category")}>
          {CATEGORIES.map((c) => <option key={c} value={c}>{t("cats")[c]}</option>)}
        </select>
        <input placeholder={t("writer.tags")} value={post.tags} onChange={set("tags")} data-testid="blog-tags-input" />
      </div>
      <textarea required placeholder={t("writer.content")} value={post.content} onChange={set("content")} data-testid="blog-content-input" />
      <div className="writer-grid">
        <input placeholder={t("writer.titleHi")} value={post.title_hi} onChange={set("title_hi")} data-testid="blog-title-hi-input" />
        <input placeholder={t("writer.excerptHi")} value={post.excerpt_hi} onChange={set("excerpt_hi")} data-testid="blog-excerpt-hi-input" />
      </div>
      <textarea placeholder={t("writer.contentHi")} value={post.content_hi} onChange={set("content_hi")} data-testid="blog-content-hi-input" />
      <div className="writer-actions">
        <button className="btn btn-dark" type="submit" disabled={busy} data-testid="publish-blog-button">{t("writer.publish")}</button>
        <button className="text-link" type="button" onClick={onCancel} data-testid="cancel-blog-button">{t("writer.cancel")}</button>
      </div>
    </form>
  );
}

export default function BlogList() {
  const { t, lang } = useLang();
  const [posts, setPosts] = useState([]);
  const [query, setQuery] = useState("");
  const [showWriter, setShowWriter] = useState(false);
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "All";

  useEffect(() => {
    api.get("/blogs").then((r) => setPosts(r.data || [])).catch(() => {});
  }, []);

  const filtered = useMemo(
    () =>
      posts.filter((p) => {
        const title = (lang === "hi" && p.title_hi ? p.title_hi : p.title) || "";
        const hay = `${title} ${p.excerpt} ${p.excerpt_hi || ""} ${p.category} ${(p.tags || []).join(" ")}`.toLowerCase();
        return (cat === "All" || p.category === cat) && hay.includes(query.toLowerCase());
      }),
    [posts, query, cat, lang]
  );

  return (
    <div data-testid="blog-page">
      <Seo title="The GST Journal — RKJ GST Consultancy" description="Daily, plain-language articles on GST, income tax, compliance deadlines and business tips from RKJ GST Consultancy. In English and Hindi." />
      <section className="page-head section-wrap">
        <div className="page-head-copy">
          <Reveal><span className="eyebrow">{t("blog.eyebrow")}</span></Reveal>
          <Reveal delay={0.08}><h1>{t("blog.title")}</h1></Reveal>
          <Reveal delay={0.15}><p>{t("blog.sub")}</p></Reveal>
        </div>
        <Reveal delay={0.2} className="page-head-image">
          <img src={docsImage} alt={t("blog.imgAlt")} data-testid="blog-head-image" />
        </Reveal>
      </section>

      <section className="section-wrap blog-browser">
        <div className="blog-toolbar">
          <div className="filter-row" data-testid="category-filters">
            {["All", ...CATEGORIES].map((c) => (
              <button
                key={c}
                className={cat === c ? "filter-chip active" : "filter-chip"}
                onClick={() => setParams(c === "All" ? {} : { cat: c })}
                data-testid={`category-filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {c === "All" ? t("blog.all") : t("cats")[c]}
              </button>
            ))}
          </div>
          <div className="blog-actions">
            <div className="search-box">
              <Search size={16} />
              <input placeholder={t("blog.search")} value={query} onChange={(e) => setQuery(e.target.value)} data-testid="blog-search-input" />
            </div>
            <button className="btn btn-dark" onClick={() => setShowWriter(!showWriter)} data-testid="open-blog-writer-button">
              {showWriter ? <X size={16} /> : <PenLine size={16} />} {t("blog.write")}
            </button>
          </div>
        </div>

        {showWriter && (
          <Writer
            onPublished={(post) => { setPosts([post, ...posts]); setShowWriter(false); }}
            onCancel={() => setShowWriter(false)}
          />
        )}

        {filtered.length === 0 ? (
          <p className="blog-empty" data-testid="blog-empty-state">{t("blog.empty")}</p>
        ) : (
          <div className="blog-grid">
            {filtered.map((post, i) => <BlogCard key={post.id} post={post} index={i} />)}
          </div>
        )}
      </section>
    </div>
  );
}
